import * as swcHelpers from '@swc/helpers';
import { getAttrValue, getLocalizedValue, getFrontImage, getLocalizedAttrValues, isString, getLocalizedAttrValue } from '@grandvisionhq/utils';
import { getActiveVariant, getPrice, getMinimumSupplement } from '../helpers.js';

const reduceVariantInfo = (locale)=>(mem, variant)=>{
        if (!variant.sku || !variant.isMatchingVariant || !variant.attributes) {
            return mem;
        }
        const variationAttribute = getAttrValue(variant, 'variationAttribute');
        if (!variationAttribute || !isString(variationAttribute)) {
            return mem;
        }
        const title = getLocalizedAttrValue(variant, variationAttribute, locale);
        if (!title) {
            return mem;
        }
        const isDefault = getAttrValue(variant, 'default') === true;
        return [
            ...mem,
            swcHelpers.objectSpread({
                sku: variant.sku,
                title,
                isDefault
            }, variant.images && {
                image: variant.images[0],
                image_v2: variant.images[1]
            }), 
        ];
    }
;
const getVariants = (product, locale)=>{
    const productHasOnlyMasterVariant = !product.variants.length;
    return productHasOnlyMasterVariant ? {} : {
        variants: [
            product.masterVariant,
            ...product.variants
        ].reduce(reduceVariantInfo(locale), [])
    };
};
const getBaseInfo = (product, sku, locale)=>{
    var ref, ref1, ref2, ref3, ref4, ref5;
    const variant = getActiveVariant(product, sku);
    const isMasterVariant = product.masterVariant.sku === variant.sku;
    const productHasOnlyMasterVariant = !product.variants.length;
    let isDefault = false;
    // We only want to consider the "default" value of a master variant if the product doesn't have any variants
    if (isMasterVariant && productHasOnlyMasterVariant) {
        isDefault = getAttrValue(variant, 'default') === true;
    } else if (!isMasterVariant) {
        isDefault = getAttrValue(variant, 'default') === true;
    }
    let rootImage;
    var _images;
    // We always take the root image from the masterVariant
    const [firstMasterImage, secondMasterImage] = (_images = product.masterVariant.images) !== null && _images !== void 0 ? _images : [];
    var _images1;
    const [variantImage] = (_images1 = variant.images) !== null && _images1 !== void 0 ? _images1 : [];
    const image_v2 = productHasOnlyMasterVariant ? firstMasterImage : secondMasterImage;
    // If it has variants the second image is the root image
    if (!isMasterVariant) {
        // If the SKU isn't the masterVariant's SKU we're constructing this object for when a variant has been selected
        rootImage = variantImage;
    } else {
        rootImage = productHasOnlyMasterVariant ? firstMasterImage : secondMasterImage;
    }
    const discountDescription = (ref = variant.prices) === null || ref === void 0 ? void 0 : (ref1 = ref[0]) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.discounted) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.discount) === null || ref3 === void 0 ? void 0 : (ref4 = ref3.obj) === null || ref4 === void 0 ? void 0 : ref4.description;
    const discountText = discountDescription ? getLocalizedValue(discountDescription, locale) : undefined;
    // converting the tags[].value to a localized string
    const tags = (ref5 = product.masterVariant.tags) === null || ref5 === void 0 ? void 0 : ref5.map(({ key , value  })=>({
            key,
            value: typeof value === 'string' ? value : value[locale]
        })
    ).filter(({ value  })=>!!value
    );
    const colorCode = getAttrValue(variant, 'colorCode');
    const transitionType = getAttrValue(variant, 'transitionType');
    return swcHelpers.objectSpread({
        sku,
        title: getLocalizedValue(product.name, locale),
        description: product.description ? getLocalizedValue(product.description, locale) : undefined,
        price: getPrice(variant),
        minimumSupplement: getMinimumSupplement(variant),
        isDefault,
        colorCode,
        transitionType
    }, tags && {
        tags
    }, discountText && {
        discountText
    }, rootImage && {
        image: rootImage
    }, image_v2 && {
        image_v2
    });
};
const getUsps = (product, sku, locale)=>{
    const variant = getActiveVariant(product, sku);
    return getLocalizedAttrValues(variant, 'prescriptionGlassPackageUsps', locale);
};
const convertToFrameInfo = (product, sku, locale)=>{
    const variant = getActiveVariant(product, sku);
    const brand = typeof getAttrValue(variant, 'brand') !== 'object' ? String(getAttrValue(variant, 'brand')) : '';
    const codeAttr = getAttrValue(variant, 'brandModelCode');
    const code = typeof codeAttr === 'string' ? codeAttr : '';
    const title = getLocalizedValue(product.name, locale) || [
        brand,
        code
    ].filter(Boolean).join(' ');
    return {
        brand,
        code,
        price: getPrice(variant),
        sku,
        title
    };
};
const convertToLensTypeInfo = (product, sku, locale)=>swcHelpers.objectSpread({}, getBaseInfo(product, sku, locale), getVariants(product, locale))
;
const convertToPackageInfo = (product, sku, locale)=>{
    var ref, ref6;
    const variant = getActiveVariant(product, sku);
    var ref7;
    return swcHelpers.objectSpread({}, getBaseInfo(product, sku, locale), {
        usps: getUsps(product, sku, locale),
        areAddOnsOptional: (ref7 = (ref6 = (ref = variant.attributes) === null || ref === void 0 ? void 0 : ref.find((a)=>a.name === 'areAddonsOptional'
        )) === null || ref6 === void 0 ? void 0 : ref6.value) !== null && ref7 !== void 0 ? ref7 : true
    });
};
const convertToLensAddOnInfo = (product, sku, locale)=>swcHelpers.objectSpread({}, getBaseInfo(product, sku, locale), getVariants(product, locale), {
        productKey: product.key
    })
;
const lowToHigh = (extractPrice)=>(left, right)=>{
        return (extractPrice(left) || 0) - (extractPrice(right) || 0);
    }
;
const getProductImageData = (product, sku, width)=>{
    var ref;
    const variant = getActiveVariant(product, sku);
    const calculatedWidth = Math.floor(width);
    const image = getFrontImage((ref = variant.media) === null || ref === void 0 ? void 0 : ref.images);
    return {
        url: `${image.url}?w=${calculatedWidth}&fm=jpeg&auto=format`,
        dimensions: image.dimensions
    };
};

export { convertToFrameInfo, convertToLensAddOnInfo, convertToLensTypeInfo, convertToPackageInfo, getProductImageData, lowToHigh, reduceVariantInfo };
//# sourceMappingURL=helpers.js.map
